import { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { updateShiftClockStatus } from '../../../redux/shifts/shiftsSlice';
import { startShiftThunk, endShiftThunk } from '../../../redux/shifts/shiftsThunks';
import { LOADING_STATES } from '../../../utils/constants/loadingStates';

/**
 * Custom hook for managing shift clock-in and clock-out operations
 * @returns {Object} An object containing:
 *   - error: string - Current error message if any
 *   - setError: function - Function to set error message
 *   - handleStartShift: function - Function to start a shift
 *   - handleEndShift: function - Function to end a shift
 *   - handleStartBreak: function - Function to start a break
 *   - handleEndBreak: function - Function to end a break
 *   - handleWaiveBreak: function - Function to waive a break
 *   - canTakeBreak: function - Function to check if a break can be taken
 */
export const useShiftClockInManagement = () => {
  const [error, setError] = useState('');
  const pendingRequests = useRef(new Set());
  const dispatch = useDispatch();

  const handleShiftAction = async (shift, actionType) => {
    if (!shift?.id) {
      throw new Error('No shift ID provided');
    }

    // Create a unique request ID
    const requestId = `${actionType}-${shift.id}`;

    // Check if this request is already in progress
    if (pendingRequests.current.has(requestId)) {
      return;
    }

    // Add to pending requests
    pendingRequests.current.add(requestId);

    try {
      // Using Redux thunks instead of direct Firebase calls
      if (actionType === 'start-shift') {
        await dispatch(startShiftThunk(shift.id));
      } else if (actionType === 'end-shift') {
        await dispatch(endShiftThunk(shift.id));
      } else {
        throw new Error(`Invalid action type: ${actionType}`);
      }

      setError(''); // Clear error on success
    } catch (error) {
      const errorMessage = error?.message || `Failed to perform ${actionType}`;
      setError(errorMessage);
    } finally {
      // Remove from pending requests
      pendingRequests.current.delete(requestId);
    }
  };

  const handleStartShift = (shift) => handleShiftAction(shift, 'start-shift');
  const handleEndShift = (shift) => handleShiftAction(shift, 'end-shift');

  /**
   * Check if a break can be taken
   * @param {Object} shift - The shift object
   * @param {string} breakType - The type of break ('MEAL' or 'REST')
   * @returns {Promise<boolean>} - Whether the break can be taken
   */
  const canTakeBreak = async (shift, breakType) => {
    if (!shift?.id || !breakType) {
      setError('Missing shift ID or break type');
      return false;
    }

    try {
      const checkBreak = httpsCallable(functions, 'canTakeBreak');
      const result = await checkBreak({
        sid: shift.id,
        breakType,
      });
      return result.data.isValid;
    } catch (error) {
      setError(error?.message || 'Failed to check break availability');
      return false;
    }
  };

  /**
   * Start a break
   * @param {Object} shift - The shift object
   * @param {string} breakType - The type of break ('MEAL_BREAK' or 'REST_BREAK')
   */
  const handleStartBreak = async (shift, breakType) => {
    if (!shift?.id) {
      setError('No shift ID provided');
      return;
    }

    if (!breakType || !['MEAL_BREAK', 'REST_BREAK'].includes(breakType)) {
      setError('You must pass a valid breakType (MEAL_BREAK or REST_BREAK)');
      return;
    }

    const requestId = `start-break-${shift.id}`;
    if (pendingRequests.current.has(requestId)) {
      return;
    }

    pendingRequests.current.add(requestId);
    dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.LOADING }));

    try {
      const startBreakFn = httpsCallable(functions, 'startBreak');
      const result = await startBreakFn({
        sid: shift.id,
        breakType,
      });

      // Update the shift data in Redux
      if (result.data) {
        dispatch({
          type: 'shifts/updateShift',
          payload: {
            id: shift.id,
            updatedShiftRedux: result.data,
          },
        });
      } else {
        console.debug('No shift data received from startBreak function');
      }

      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.SUCCEEDED }));
      setError('');
    } catch (error) {
      console.error('Error starting break:', error);
      setError(error?.message || 'Failed to start break');
      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.FAILED }));
    } finally {
      pendingRequests.current.delete(requestId);
    }
  };

  /**
   * End a break
   * @param {Object} shift - The shift object
   * @param {string} breakId - The ID of the break to end
   */
  const handleEndBreak = async (shift, breakId) => {
    if (!shift?.id || !breakId) {
      setError('Missing shift ID or break ID');
      return;
    }

    const requestId = `end-break-${shift.id}-${breakId}`;
    if (pendingRequests.current.has(requestId)) {
      return;
    }

    pendingRequests.current.add(requestId);
    dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.LOADING }));

    try {
      const endBreakFn = httpsCallable(functions, 'endBreak');
      const result = await endBreakFn({
        sid: shift.id,
        bid: breakId,
      });

      // Update the shift data in Redux
      if (result.data) {
        dispatch({
          type: 'shifts/updateShift',
          payload: {
            id: shift.id,
            updatedShiftRedux: result.data,
          },
        });
      }

      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.SUCCEEDED }));
      setError('');
    } catch (error) {
      console.error('Error ending break:', error);
      setError(error?.message || 'Failed to end break');
      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.FAILED }));
    } finally {
      pendingRequests.current.delete(requestId);
    }
  };

  const handleWaiveBreak = async (shift, breakData, reason) => {
    if (!shift?.id || !breakData?.id) {
      setError('Missing shift ID or break data');
      return;
    }

    try {
      setError('');
      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.LOADING }));
      const waiveBreak = httpsCallable(functions, 'waiveBreak');
      await waiveBreak({
        sid: shift.id,
        bid: breakData.id,
        reason,
      });
      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.SUCCEEDED }));
    } catch (err) {
      console.error('Error in handleWaiveBreak:', err);
      setError('Failed to waive break');
      dispatch(updateShiftClockStatus({ id: shift.id, status: LOADING_STATES.FAILED }));
    }
  };

  return {
    error,
    setError,
    handleStartShift,
    handleEndShift,
    handleStartBreak,
    handleEndBreak,
    handleWaiveBreak,
    canTakeBreak,
  };
};
